import { CommonModule } from '@angular/common'
import { Component, DestroyRef, computed, inject, input, output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { filter } from 'rxjs/operators'

import { FlxModalService } from '@flowx/angular-ui-toolkit'

import { showModalAboveAiPanel } from '../../../../../shared/utils/modal.utils'
import { AI_DM_SOURCE_MARKER, AiDmKeyRow, AiDmKeyRowStatus } from '../ai-dm-key-row.model'
import { AiDmModifyModalComponent } from '../ai-dm-modify-modal/ai-dm-modify-modal.component'

/**
 * Presentational summary of the AI-proposed data-model changes, shared by the designer & developer
 * agents. Groups rows by status (Used / Created) and surfaces opted-out keys, with a source-coloured
 * marker per row. Opening "Modify Keys" relays the edited rows back via `rowsUpdate`; persistence is
 * the caller's responsibility (the agent knows how to commit its own domain model).
 */
@Component({
  selector: 'flx-ai-dm-summary',
  templateUrl: './ai-dm-summary.component.html',
  host: { class: 'block' },
  imports: [CommonModule],
})
export class AiDmSummaryComponent {
  #modalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)

  rows = input<AiDmKeyRow[]>([])
  isActiveOutcome = input<boolean>(false)
  rowsUpdate = output<AiDmKeyRow[]>()

  sourceMarkerClass = AI_DM_SOURCE_MARKER

  usedRows = computed(() =>
    this.rows().filter((row) => row.active && row.status === AiDmKeyRowStatus.Used)
  )
  createdRows = computed(() =>
    this.rows().filter((row) => row.active && row.status === AiDmKeyRowStatus.Created)
  )
  optedOutRows = computed(() => this.rows().filter((row) => !row.active))

  openModifyModal(): void {
    const modal = showModalAboveAiPanel(
      this.#modalService,
      AiDmModifyModalComponent,
      { closeOnBackdropClick: false, appendTo: 'body' },
      { rows: this.rows() }
    )

    modal.data$
      .pipe(
        filter(({ confirm }) => !!confirm),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ rows }) => this.rowsUpdate.emit(rows))
  }
}
